import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext';
import { ApiError, meApi } from '../lib/api';
import { Alert, Button, Input, Label, Modal, Spinner } from './ui';

/**
 * Pflicht-Dialog beim ersten Login ohne hinterlegten Namen: fragt Vor-/Nachname ab, damit im
 * Dashboard angezeigt werden kann, wer einen Container eingetragen hat. Lässt sich nicht
 * wegklicken (onClose = no-op) — der Name ist erforderlich.
 */
export function ProfileSetupModal({
  open,
  onSaved,
}: {
  open: boolean;
  onSaved: () => Promise<void> | void;
}): JSX.Element {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const canSave = firstName.trim().length > 0 && lastName.trim().length > 0;

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!canSave) return;
    setSaving(true);
    setError('');
    try {
      await meApi.updateProfile({ firstName: firstName.trim(), lastName: lastName.trim() }, token);
      await onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('profile.error'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => undefined}
      size="sm"
      title={t('profile.title')}
      footer={
        <Button type="submit" form="ctp-profile-form" disabled={saving || !canSave}>
          {saving ? <Spinner /> : t('profile.save')}
        </Button>
      }
    >
      <form id="ctp-profile-form" onSubmit={onSubmit} className="space-y-4">
        <p className="text-sm text-muted-foreground">{t('profile.hint')}</p>
        {error && <Alert kind="error">{error}</Alert>}
        <div>
          <Label htmlFor="ctp-first">{t('profile.firstName')}</Label>
          <Input
            id="ctp-first"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            autoFocus
            required
          />
        </div>
        <div>
          <Label htmlFor="ctp-last">{t('profile.lastName')}</Label>
          <Input
            id="ctp-last"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
          />
        </div>
      </form>
    </Modal>
  );
}
